import * as Sentry from '@sentry/browser';
import {
  CartCustomer,
  CartProduct,
  calculateTotals as calculateTotalsFromCart,
  Discount,
  isApplicableForMarket,
  isCouponApplicableForMarket,
  ProductRecord,
  SKU
} from '@shared/cart-js';
import {
  AppliedTotals,
  AvailableCoupon,
  CartPayload
} from '@shared/cart-js/src/schemas';
import { localStore } from '@shared/storage-js';
import { roundTo } from '@shared/utils-js';
import axios from 'axios';
import { isEqual } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import keyBy from 'lodash/keyBy';
import { v4 as uuid } from 'uuid';
import {
  calculateCartDiscountAmounts,
  DiscountBreakdown,
  generateDiscountBadge,
  missingInitialProducts
} from './functions';
import {
  cartHasCameras,
  DISCONTINUED_PRODUCTS_MAP,
  isBasicMonitoringPlan,
  migrateCartData
} from './helpers';
import {
  DatoData,
  CartAlternativePayment,
  SavedCart,
  SavedCartTotals
} from './schemas';

const PKG_PRODUCTS = [
  'CPA-PANEL-345-1',
  'CPA-HUB-345-1',
  'window-sticker-sm',
  'CP-YS-23i-1'
];

const MAX_ZONE_COUNT = 96;

/** Product-level promo that makes up to maxQuantity units free (100% off). */
function getFreeUnitsProductDiscount(
  discountIds: string[],
  discounts: Discount[]
): Discount | undefined {
  return discountIds
    .map((id) => discounts.find((discount) => discount.id === id))
    .find(
      (discount) =>
        discount?.discountType === 'percentage' &&
        discount.discountAmount === 100 &&
        (discount.maxQuantity ?? 0) >= 1
    );
}

type CoveCartFromBackend = Omit<SavedCart, 'totals'> & {
  totals: SavedCartTotals;
};

/** Prefer the normalized record; else rebuild it from legacy Affirm/PayPal fields. */
const resolveAlternativePayment = ({
  alternativePayment,
  affirmId,
  affirmAmount,
  affirmExpires,
  paypalOrderId,
  paypalAmount,
  paypalExpires
}: {
  alternativePayment?: CartAlternativePayment | null;
  affirmId?: string;
  affirmAmount?: number;
  affirmExpires?: number;
  paypalOrderId?: string;
  paypalAmount?: number;
  paypalExpires?: number;
}): CartAlternativePayment | null => {
  if (alternativePayment?.id && alternativePayment.amount > 0) {
    return alternativePayment;
  }
  if (affirmId && affirmAmount && affirmAmount > 0) {
    return {
      option: 'affirm',
      provider: 'affirm',
      id: affirmId,
      amount: affirmAmount,
      expiresAt: affirmExpires ?? 0
    };
  }
  if (paypalOrderId && paypalAmount && paypalAmount > 0) {
    return {
      option: 'paypal',
      provider: 'paypal',
      id: paypalOrderId,
      amount: paypalAmount,
      expiresAt: paypalExpires ?? 0
    };
  }
  return null;
};

const syncLegacyAltPaymentFields = (
  target: {
    affirmId?: string;
    affirmAmount?: number;
    affirmExpires?: number;
    paypalOrderId?: string;
    paypalAmount?: number;
    paypalExpires?: number;
  },
  payment: CartAlternativePayment | null
) => {
  if (!payment) {
    target.affirmId = undefined;
    target.affirmAmount = undefined;
    target.affirmExpires = undefined;
    target.paypalOrderId = undefined;
    target.paypalAmount = undefined;
    target.paypalExpires = undefined;
    return;
  }
  if (payment.provider === 'affirm') {
    target.affirmId = payment.id;
    target.affirmAmount = payment.amount;
    target.affirmExpires = payment.expiresAt;
    target.paypalOrderId = undefined;
    target.paypalAmount = undefined;
    target.paypalExpires = undefined;
    return;
  }
  target.paypalOrderId = payment.id;
  target.paypalAmount = payment.amount;
  target.paypalExpires = payment.expiresAt;
  target.affirmId = undefined;
  target.affirmAmount = undefined;
  target.affirmExpires = undefined;
};

type CoveCart = {
  apiV4Url?: string;
  loadRemote?: boolean;
  cartId?: string;
  salesRepId?: string | null;
  referralCode?: string;
  debug?: boolean;
  cart?: any;
  updateCallback?: (cart: any) => void;
  datoData?: any;
  saveCart?: (
    cartId: string,
    body: any,
    options: { apiV4Url?: string }
  ) => Promise<{
    data: CoveCartFromBackend;
  }>;
  shippingMethod?: string;
  prefixLocalStorageKey?: string;
  efTransactionId?: string;
  country: 'US' | 'CA';
};

const coveCart = async ({
  apiV4Url,
  loadRemote,
  cartId,
  salesRepId,
  referralCode,
  debug = false,
  cart,
  updateCallback,
  datoData,
  saveCart = defaultSaveCart,
  shippingMethod = 'FREE-Regular',
  prefixLocalStorageKey,
  efTransactionId,
  country = 'US'
}: CoveCart) => {
  let loading = true;
  const lastSaveDuration: number = 0;
  let newCartId;
  let cartItemIds: Array<string> | undefined;
  let cartProducts: Record<SKU, CartProduct> | undefined;
  let customer: CartCustomer = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postcode: ''
  };
  let products: Record<SKU, ProductRecord> | undefined;
  // let subscriptionIds;
  // let payLaterSubscriptionIds;
  // let valueSubscriptionIds;
  let subscriptions;
  let discounts: DatoData['discounts'];
  let discountBreakdown: DiscountBreakdown | undefined;
  let payLater: DatoData['payLater'];
  let appliedDiscounts;
  let monitoringPlan: CartProduct | null = null;
  let equipmentTaxRate;
  let monitoringTaxRate;
  let taxCalculated;
  let taxPostcode;
  let isPayLater;
  let isCartSaved;
  let isQuizSkipped;
  let isValue;
  let isPremium;
  let availableCoupons;
  let coupons;
  let couponCodes;
  let couponCodeData;
  /**
   * @returns {Object<{
   *  thanks: boolean,
   *  benefits: boolean,
   *  type: string,
   * }>}
   */
  let exclusiveType;
  // Deprecated promo fields
  let promoCode;
  let promoCodeData = {};
  let promoCodeDescription: Array<string> = [];
  let promoCodeDiscounts: Array<string> = [];
  let promoCodeExclusive = false;
  let promoCodeValid;
  // End deprecated promo fields
  let hasExclusiveCoupon;
  let hasValidCouponCode;
  let quizData;
  let freeMonths;
  let isReferral;
  let noSensors;
  let totals: AppliedTotals | undefined;
  let persistedCartData: SavedCart | undefined;
  let isPayLaterEligible;
  let blacklistedPostcode;
  let returningUser: boolean | null = false;
  let surveysTaken: Array<string> = [];
  let cameraType: Array<string> = [];
  let applyCoveCreditAmount;
  let affirmId;
  let affirmAmount;
  let affirmExpires;
  let paypalOrderId;
  let paypalAmount;
  let paypalExpires;
  let alternativePayment: CartAlternativePayment | null | undefined;
  let migratedToAlula = false;
  let zoneCount = 0;
  let showZoneCountWarning = false;
  let zoneCountWarningDismissed = false;
  let orderTotals;
  let giveawayMultiplier: number | undefined = 1;
  let conversionRates: DatoData['conversionRates'];

  const cartIdLocalStorageKey = `${prefixLocalStorageKey || ''}CART_ID`;
  const cartLocalStorageKey = `${prefixLocalStorageKey || ''}coveCart`;

  let availableCouponsFromDato: Array<AvailableCoupon> = [];

  ({
    products,
    discounts,
    payLater,
    availableCoupons: availableCouponsFromDato,
    giveawayMultiplier,
    conversionRates
  } = (await new Promise((res, rej) => {
    try {
      res(datoData);
    } catch (error) {
      rej(error);
    }
  })?.catch?.((err) => {
    Sentry.captureException(err);
  })) as DatoData);

  if (!cartId) {
    log('Creating new cart id');
    newCartId = uuid();
  } else if (cartId && loadRemote) {
    log('Loading cart from api');
    // load from api
    const response = await axios
      .get(`${apiV4Url}/cart/${cartId}`, {
        headers: {
          'content-type': 'application/json'
        }
      })
      .then(({ data }) => data)
      .catch((err) => {
        /* log to loggly */
        Sentry.captureException(err);
        return null;
      });
    persistedCartData = response as SavedCart;
  } else if (cart) {
    log('Loading existing cart');
    persistedCartData = cart as SavedCart;
  } else if (
    typeof window !== 'undefined' &&
    localStore.getItem(cartLocalStorageKey)
  ) {
    log('Loading cart from local storage');
    persistedCartData = JSON.parse(
      localStore.getItem(cartLocalStorageKey)
    ) as SavedCart;
  }

  loading = false;

  const paramCartId = cartId;
  const paramSalesRepId = salesRepId;
  const paramReferralCode = referralCode;

  const availableCouponsBreakdown = calculateCartDiscountAmounts({
    appliedDiscounts: [],
    discounts: [],
    coupons: [],
    availableCoupons: availableCoupons || [],
    cartProducts: {},
    totals: {}
  }).availableCouponsBreakdown;

  availableCoupons = availableCouponsBreakdown;

  if (
    persistedCartData &&
    !isUndefined(products) &&
    !isUndefined(persistedCartData) &&
    !isUndefined((persistedCartData as SavedCart).cartProducts)
  ) {
    Object.keys((persistedCartData as SavedCart).cartProducts)?.forEach?.(
      (key) => {
        if (products) {
          const sku = DISCONTINUED_PRODUCTS_MAP[key] ?? key;
          const product = products[sku];
          if (product) {
            product.qty =
              persistedCartData.cartProducts[sku]?.qty ??
              products?.[key]?.qty ??
              0;
          }
        }
      }
    );

    persistedCartData.products = products;
    persistedCartData.discounts = discounts;
    persistedCartData.payLater = payLater;
    persistedCartData.availableCoupons = availableCouponsBreakdown;

    ({
      cartId,
      salesRepId,
      referralCode,
      cartItemIds,
      cartProducts,
      customer,
      products,
      subscriptions,
      discounts,
      discountBreakdown,
      payLater,
      appliedDiscounts,
      monitoringPlan,
      equipmentTaxRate,
      monitoringTaxRate,
      taxCalculated,
      taxPostcode,
      // subscriptionIds,
      // payLaterSubscriptionIds,
      // valueSubscriptionIds,
      isPayLater,
      isCartSaved,
      isQuizSkipped,
      isValue,
      isPremium,
      coupons,
      couponCodes,
      couponCodeData,
      exclusiveType,
      hasExclusiveCoupon,
      hasValidCouponCode,
      promoCode,
      promoCodeData,
      promoCodeDescription,
      promoCodeDiscounts,
      promoCodeExclusive,
      promoCodeValid,
      quizData,
      freeMonths,
      isReferral,
      noSensors,
      totals,
      isPayLaterEligible,
      blacklistedPostcode,
      returningUser,
      surveysTaken,
      cameraType,
      affirmId,
      affirmAmount,
      affirmExpires,
      paypalOrderId,
      paypalAmount,
      paypalExpires,
      alternativePayment,
      migratedToAlula = false,
      applyCoveCreditAmount,
      zoneCount,
      showZoneCountWarning,
      zoneCountWarningDismissed,
      orderTotals
    } = persistedCartData as SavedCart);
  }

  const resolvedAlternativePayment = resolveAlternativePayment({
    alternativePayment,
    affirmId,
    affirmAmount,
    affirmExpires,
    paypalOrderId,
    paypalAmount,
    paypalExpires
  });
  const legacyAltPaymentFields = {
    affirmId: undefined as string | undefined,
    affirmAmount: undefined as number | undefined,
    affirmExpires: undefined as number | undefined,
    paypalOrderId: undefined as string | undefined,
    paypalAmount: undefined as number | undefined,
    paypalExpires: undefined as number | undefined
  };
  syncLegacyAltPaymentFields(
    legacyAltPaymentFields,
    resolvedAlternativePayment
  );

  let AUTO_SAVE_QUEUE_FN: ReturnType<typeof setTimeout> | null = null;

  const Cart = {
    loading: loading,
    lastSaveDuration: lastSaveDuration,
    cartId: paramCartId || cartId || newCartId,
    salesRepId: paramSalesRepId || salesRepId,
    referralCode: paramReferralCode || referralCode,
    /**
     * @type {Array<string>}
     */
    cartItemIds: cartItemIds || [],
    /**
     * @type {Record<string, import('@shared/cart-js/src/schemas').CartProduct>=}
     */
    cartProducts: cartProducts || {},
    /**
     * @type {Record<string, import('@shared/cart-js/src/schemas').CartCustomer>=}
     */
    customer: customer || {},
    /**
     * @type {Record<string, import('@shared/cart-js/src/schemas').ProductRecord & {qty?: number}>=}
     */
    products: products || {},
    subscriptions: subscriptions || {},
    /**
     * Array of discount records.
     * @type {Array<import('@shared/cart-js/src/schemas').Discount>}
     */
    discounts: discounts || [],
    discountBreakdown: discountBreakdown || [],
    payLater: payLater || {},
    /**
     * Array of discount records.
     * @type {Array<string>}
     */
    appliedDiscounts: appliedDiscounts || [],
    availableCoupons: availableCouponsBreakdown || [],
    monitoringPlan: monitoringPlan,
    equipmentTaxRate: equipmentTaxRate || 0,
    monitoringTaxRate: monitoringTaxRate || 0,
    taxCalculated: taxCalculated || false,
    taxPostcode: taxPostcode || '',
    subscriptionIds: ['vm-pn', 'vm-pl', 'bm-pl', 'bm-pn', 'pm-pn'],
    payLaterSubscriptionIds: ['vm-pl', 'bm-pl'],
    valueSubscriptionIds: ['vm-pn', 'vm-pl'],
    premiumSubscriptionIds: ['pm-pn'],
    isPayLater: isPayLater || false,
    isCartSaved: isCartSaved || false,
    isQuizSkipped: isQuizSkipped || false,
    isPremium: isPremium || false,
    isValue: isValue || false,
    /**
     * @type {Array<import('@shared/cart-js/src/schemas').DatoCMSCoupon>=}
     */
    coupons: coupons || [],
    /**
     * @type {Array<string>}
     */
    couponCodes: couponCodes || [],
    /**
     * @type {Record<string, import('@shared/cart-js/src/schemas').DatoCMSCoupon>=}
     */
    couponCodeData: couponCodeData || {},
    exclusiveType: exclusiveType || {
      thanks: false,
      benefits: false,
      type: ''
    },
    hasExclusiveCoupon: hasExclusiveCoupon || false,
    hasValidCouponCode: hasValidCouponCode || false,
    promoCode: promoCode || null,
    promoCodeData: promoCodeData,
    /**
     * @type {Array<string>}
     */
    promoCodeDescription: promoCodeDescription || [],
    promoCodeDiscounts: promoCodeDiscounts || [],
    promoCodeValid: promoCodeValid || null,
    promoCodeExclusive: promoCodeExclusive || false,
    quizData: quizData || null,
    freeMonths: freeMonths || null,
    isReferral: isReferral || false,
    noSensors: noSensors || false,
    orderTotals: orderTotals || null,
    totals: totals,
    autoSaveQueuedFn: null,
    isPayLaterEligible: isPayLaterEligible || true,
    blacklistedPostcode: blacklistedPostcode || false,
    returningUser: returningUser ?? null,
    surveysTaken: surveysTaken || [],
    cameraType: cameraType || [],
    applyCoveCreditAmount: applyCoveCreditAmount || 0,
    alternativePayment: resolvedAlternativePayment,
    affirmId: legacyAltPaymentFields.affirmId,
    affirmAmount: legacyAltPaymentFields.affirmAmount,
    affirmExpires: legacyAltPaymentFields.affirmExpires,
    paypalOrderId: legacyAltPaymentFields.paypalOrderId,
    paypalAmount: legacyAltPaymentFields.paypalAmount,
    paypalExpires: legacyAltPaymentFields.paypalExpires,
    migratedToAlula: migratedToAlula,
    shippingMethod,
    efTransactionId: efTransactionId,
    zoneCount,
    showZoneCountWarning,
    zoneCountWarningDismissed,
    giveawayMultiplier,
    conversionRates: conversionRates || [],
    country: country || 'US',
    displayPricingCache: {} as Record<
      string,
      {
        revision: string;
        retail: number;
        sale: number;
        appliedDiscountIds: string[];
      }
    >,
    displayPricingRevision: null as string | null,
    hasAdditionalSensors() {
      const sensorSkus = [
        'CP-DS-345-1',
        'CP-WS-345-1',
        'CP-PIR-345-1',
        '2G-GB-345-1',
        '2GIG-SMKT8-345',
        '2GIG-CO8-345',
        'CP-FS-345-1'
      ];

      return this.cartItemIds.some((sku) => sensorSkus.includes(sku));
    },

    async getBannerOffer({
      email,
      country
    }: {
      email: string;
      country?: 'US' | 'CA' | null;
    }) {
      let partnerId = null;
      const result: { data: null | any; error: null | unknown | Error } = {
        data: null,
        error: null
      };
      try {
        if (typeof window !== 'undefined') {
          partnerId = localStore.getItem('partnerId');
        }
        const emailresponse = await fetch(
          `${apiV4Url}/email/subscription/banner-offer-new/`,
          {
            headers: {
              'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify({
              email,
              country,
              cartId: this.cartId,
              partnerId
            })
          }
        );

        if (!emailresponse.ok) {
          const error = await emailresponse.json();
          result.error = error;
        } else {
          this.updateCustomer({ email });
          localStorage.setItem('bannerSubscribed', 'true');
          result.data = { success: true };
        }
      } catch (err) {
        console.error(err);
        result.error = err;
      }
      return result;
    },

    /**
     * @param {number} amount
     */
    setApplyCoveCreditAmount(amount: number, getTotals = true) {
      this.clearDisplayPricingCache();
      if (!Number.isNaN(parseFloat(`${amount}`))) {
        this.applyCoveCreditAmount = Math.max(
          0,
          Number(parseFloat(`${amount}`).toFixed(2))
        );
      } else {
        this.applyCoveCreditAmount = 0;
      }
      if (getTotals) {
        const totals = this.calculateTotals(false);
        this.save();
        return totals;
      }
      return true;
    },
    updateSurveysTaken(name: string) {
      const updated = this.surveysTaken;
      if (!updated.includes(name)) {
        updated.push(name);
      }
      this.surveysTaken = updated;
    },
    async addCameraType(type: string, save = false) {
      const curr = this.cameraType || [];
      if (!curr.includes(type)) {
        curr.push(type);
      }
      this.cameraType = curr;
      await this.save(save);
    },
    removeCameraType(type: string) {
      const tIndex = this.cameraType.indexOf(type);
      if (tIndex > -1) {
        this.cameraType.splice(tIndex, 1);
      }
    },
    resetCameraType() {
      this.cameraType = [];
    },
    freeCamera() {
      const appliedDiscountsIds = this.appliedDiscounts || [];

      const eufyIndoorCameraSku = 'indoor-camera-eufy';
      const freeIndoorCameraDiscountId = '149385777';

      const freeIndoorCamera = appliedDiscountsIds.includes(
        freeIndoorCameraDiscountId
      );

      if (this.cartItemIds.includes(eufyIndoorCameraSku) && freeIndoorCamera) {
        return eufyIndoorCameraSku;
      }

      return null;
    },
    dismissZoneCountWarning() {
      this.showZoneCountWarning = false;
      this.zoneCountWarningDismissed = true;
    },
    calculateZoneCount() {
      if (!this.cartProducts || !this.products) {
        return 0;
      }

      const zoneCount = Object.values(this.cartProducts).reduce(
        (total, product) => {
          if (!product.sku || !this.products[product.sku]) {
            return total;
          }
          const zoneCount = this.products[product.sku].zones;
          return total + (zoneCount || 0) * product.qty;
        },
        0
      );

      if (zoneCount > MAX_ZONE_COUNT) {
        this.showZoneCountWarning = !this.zoneCountWarningDismissed;
      } else {
        this.showZoneCountWarning = false;
      }

      this.zoneCount = zoneCount;
      return zoneCount;
    },
    updateCartItem(
      { sku: productSku, qty }: { sku: SKU; qty: number },
      getTotals = true
    ) {
      this.clearDisplayPricingCache();
      if (this.products && this.cartProducts) {
        this.zoneCountWarningDismissed = false;
        const sku = DISCONTINUED_PRODUCTS_MAP[productSku] || productSku;
        const product = this.products[sku];
        if (!product) {
          Sentry.captureException(
            new Error(`Cannot update sku (${sku}) not found in products`)
          );
          return false;
        }
        const max = product?.maximumSensorCountOnSite || 10;
        const newQty =
          parseInt(`${qty}`, 10) > max ? max : parseInt(`${qty}`, 10);

        if (this.subscriptionIds.includes(sku)) {
          // Remove all other monitoring plans
          Object.entries(this.cartProducts).forEach(([sku, cartProduct]) => {
            if (cartProduct.productType === 'subscription') {
              delete this.cartProducts[sku];
            }
          });

          const newPlanProduct = {
            ...this.products[sku],
            ...this.cartProducts[sku],
            qty: 1
          };
          // Need to update all `this` values before calling methods which persist.
          // If a method gets called before these values are changed persisted cart
          // gets out of sync.
          this.monitoringPlan = newPlanProduct;
          this.isPayLater = this.payLaterSubscriptionIds.includes(sku);
          this.isPremium = this.premiumSubscriptionIds.includes(sku);
          this.isValue = this.valueSubscriptionIds.includes(sku);
        }

        this.products[sku].qty = newQty;
        this.cartProducts[sku] = this.cartProducts[sku] || {
          ...this.products[sku]
        };
        this.cartProducts[sku].qty = newQty;

        if (newQty > 0 && this.cartItemIds.indexOf(sku) === -1) {
          this.cartItemIds.push(sku);
        }
        if (newQty < 1) {
          const i = this.cartItemIds.indexOf(sku);
          if (i > -1) {
            this.cartItemIds.splice(i, 1);
          }
          delete this.products[sku].qty;
          delete this.cartProducts[sku];
        }

        this.calculateZoneCount();
        if (getTotals) {
          const totals = this.calculateTotals(false);
          this.save();
          return totals;
        }
      }
      return true;
    },
    updateCartItems(items: Array<{ sku: SKU; qty: number }>, getTotals = true) {
      this.clearDisplayPricingCache();
      if (Array.isArray(items)) {
        for (let index = 0; index < items.length; index += 1) {
          this.updateCartItem(items[index], false);
        }
      } else {
        this.updateCartItem(items, false);
      }
      if (getTotals) {
        const totals = this.calculateTotals(false);
        this.save();
        return totals;
      }
      return true;
    },

    getTodaysGrandTotal() {
      const altAmount =
        this.alternativePayment?.amount ??
        (typeof this.affirmAmount === 'number'
          ? this.affirmAmount
          : typeof this.paypalAmount === 'number'
            ? this.paypalAmount
            : undefined);
      if (typeof altAmount === 'number') {
        return this.getGrandTotal() - altAmount;
      }
      return this.getGrandTotal();
    },

    getGrandTotal() {
      return this.totals?.totals?.orderTotalWithTax || 0;
    },
    getBillingDay() {
      const allowedRmrDays = [1, 4, 5];
      const today = new Date();
      const day = today.getDate();

      if (allowedRmrDays.includes(day)) {
        return day;
      }

      return 5;
    },
    getBillingDate() {
      const billingDay = this.getBillingDay();
      const billingDate = new Date();
      const date = billingDate.getDate();

      if (date > billingDay) {
        billingDate.setMonth(billingDate.getMonth() + 1);
      }

      billingDate.setDate(billingDay);
      return billingDate;
    },
    updateMonitoringPlan(sku: SKU, getTotals = true) {
      this.clearDisplayPricingCache();
      if (this.products) {
        if (!this.products[sku]) return { error: 'Subscription not found' };
        if (isBasicMonitoringPlan(sku) && cartHasCameras(this)) {
          return { error: 'Cameras require a Plus or Premium monitoring plan' };
        }
        const newPlanProduct = {
          ...this.products[sku],
          ...this.cartProducts[sku],
          qty: 1
        };
        // Need to update all `this` values before calling methods which persist.
        // If a method gets called before these values are changed persisted cart
        // gets out of sync.
        this.monitoringPlan = newPlanProduct;
        this.isPayLater = this.payLaterSubscriptionIds.includes(sku);
        this.isPremium = this.premiumSubscriptionIds.includes(sku);
        this.isValue = this.valueSubscriptionIds.includes(sku);
        const previousSku = this.cartItemIds.filter(
          (subSku) => this.subscriptionIds.indexOf(subSku) > -1
        )[0];

        if (previousSku && previousSku !== sku) {
          this.updateCartItem({ sku: previousSku, qty: 0 }, false);
        }
        // Persist new values
        this.updateCartItem({ sku, qty: 1 }, false);

        if (getTotals) {
          const totals = this.calculateTotals(false);
          this.save();
          return totals;
        }
      }
      return true;
    },
    async calculateTax(zip: string) {
      if (this.taxPostcode !== zip || !this.taxCalculated) {
        this.taxPostcode = zip;
        this.taxCalculated = true;
        this.save();
      }
    },
    isTaxCalculated() {
      return !!this.taxCalculated;
    },
    checkExclusiveType() {
      const coupons = this.coupons;
      let thanks = false;
      let benefits = false;
      let exclusiveType: string | undefined = '';
      const checkForOneOfExclusiveTypes = (exclusiveTypes: Array<string>) => {
        const coupon = coupons?.find((coupon) =>
          exclusiveTypes.includes(coupon?.exclusiveType ?? '-1')
        );

        if (coupon) {
          exclusiveType = coupon.exclusiveType;
        }

        return !!coupon;
      };

      benefits = checkForOneOfExclusiveTypes(['benefits']);
      thanks = checkForOneOfExclusiveTypes([
        'idme',
        'verifypass',
        'influencer'
      ]);

      this.exclusiveType = { thanks, benefits, type: exclusiveType };
    },
    async applyPromo(
      promoCode: string,
      isReferral: boolean = false
    ): Promise<{
      isValid: boolean;
      isActive: boolean;
      couponAlreadyApplied: boolean;
      allCouponDiscountsAlreadyApplied: boolean;
      someCouponDiscountsAlreadyApplied: boolean;
      missingProducts: Array<{ sku: SKU; name: string }>;
      couponExclusive: boolean;
      minimumTotal: number;
      minimumTotalCoupon: {
        couponCode: '' | string;
        priority: number;
        minimumTotal: number;
      } | null;
      orderTotalTooLow: boolean;
      hasNoEffect: boolean;
      removedCoupons: Array<string>;
      appliesTo?: Array<string>;
      notAvailableInMarket: boolean;
    }> {
      const uppercaseCode = promoCode.toUpperCase();
      const country = (this.country ?? 'US') as 'US' | 'CA';
      let couponAlreadyApplied = false;
      let allCouponDiscountsAlreadyApplied = false;
      let someCouponDiscountsAlreadyApplied = false;
      let couponExclusive = false;
      let minimumTotal = 0;
      const minimumTotalCoupon: {
        couponCode: '' | string;
        priority: number;
        minimumTotal: number;
      } | null = null;
      let missingProducts: Array<{ sku: SKU; name: string }> = [];
      let hasNoEffect = false;
      const removedCoupons: Array<string> = [];
      let orderTotalTooLow = false;
      const orderTotalBeforePromo = this.orderTotals?.orderTotalDue ?? 1000;
      let appliesTo: Array<string> = [];

      const couponForMarketCheck =
        this.couponCodeData?.[uppercaseCode] ??
        this.coupons?.find((coupon) => coupon.couponCode === uppercaseCode);

      if (
        couponForMarketCheck &&
        !isCouponApplicableForMarket(
          couponForMarketCheck,
          this.discounts || [],
          country
        )
      ) {
        return {
          isValid: !!this.couponCodeData?.[uppercaseCode],
          isActive: couponForMarketCheck.active ?? false,
          couponAlreadyApplied,
          allCouponDiscountsAlreadyApplied,
          someCouponDiscountsAlreadyApplied,
          missingProducts,
          couponExclusive,
          minimumTotal,
          minimumTotalCoupon,
          orderTotalTooLow,
          hasNoEffect,
          removedCoupons,
          notAvailableInMarket: true
        };
      }

      // Check if cart already has an exclusive coupon applied
      if (this?.coupons?.some?.((coupon) => coupon.exclusive)) {
        return {
          isValid: true,
          isActive: true,
          couponAlreadyApplied,
          allCouponDiscountsAlreadyApplied,
          someCouponDiscountsAlreadyApplied,
          missingProducts,
          couponExclusive: true,
          minimumTotal,
          minimumTotalCoupon,
          orderTotalTooLow,
          hasNoEffect,
          removedCoupons,
          notAvailableInMarket: false
        };
      }

      if (
        !!this?.couponCodeData?.[uppercaseCode]?.active &&
        this.coupons?.some((coupon) => coupon.couponCode === uppercaseCode)
      ) {
        couponAlreadyApplied = true;
        return {
          isValid: true,
          isActive: true,
          allCouponDiscountsAlreadyApplied,
          someCouponDiscountsAlreadyApplied,
          couponAlreadyApplied,
          couponExclusive,
          minimumTotal,
          minimumTotalCoupon,
          orderTotalTooLow,
          missingProducts,
          hasNoEffect,
          removedCoupons,
          notAvailableInMarket: false
        };
      }

      const prevCouponCodes = this.couponCodes;
      const prevOrderTotal = this?.totals?.totals?.orderTotal || 0;

      this.couponCodes = this.couponCodes?.length
        ? Array.from(new Set([...this.couponCodes, uppercaseCode]))
        : [uppercaseCode];
      this.clearDisplayPricingCache();

      this.isReferral = isReferral;
      // Calling save sends a request to the backend to try to apply the promo
      // The backend determines if the promo is valid and applies it to the cart
      await this.save();

      const couponDiscounts: Array<{
        couponCode: string;
        discount: string;
      }> = [];

      const appliedCouponDiscounts: Record<string, Array<string>> = {};
      this.coupons?.forEach(async (coupon) => {
        const skippedDiscounts = 0;
        const exclusive = coupon.exclusive;
        if (
          coupon.couponCode === uppercaseCode &&
          exclusive &&
          (prevCouponCodes || []).length > 0
        ) {
          couponExclusive = true;
          await this.removePromo(uppercaseCode);
        }

        const conditionalProducts: Array<SKU> = [];
        const tempBreakdown = this.calculateCouponBreakdown();

        const { totalSavings, appliesTo: appliesToCheck } =
          tempBreakdown.find((it) => it.couponCode === uppercaseCode) || {};

        hasNoEffect =
          totalSavings === 0 && !appliesToCheck?.includes('subscription');

        appliesTo = appliesToCheck || [];

        coupon.discounts.forEach(async (discount) => {
          const fullDiscount = this.discounts.find((d) => d.id === discount.id);
          [...(fullDiscount?.conditionProducts || [])].forEach((product) => {
            if (conditionalProducts.includes(product)) {
              return;
            }
            conditionalProducts.push(product);
          });

          const discountedAmount =
            tempBreakdown.find((c) => c.couponCodeId === coupon.id)
              ?.totalSavings || 0;

          // Check if the minimumOrderTotalAfterDiscount is more than the cart's total
          const minimumOrderTotalAfterDiscount =
            fullDiscount?.minimumOrderTotalAfterDiscount || 0;
          if (
            fullDiscount &&
            minimumOrderTotalAfterDiscount >
              prevOrderTotal - discountedAmount &&
            minimumOrderTotalAfterDiscount > minimumTotal
          ) {
            minimumTotal = minimumOrderTotalAfterDiscount;
          }

          if (minimumOrderTotalAfterDiscount > orderTotalBeforePromo) {
            orderTotalTooLow = true;
            await this.removePromo(uppercaseCode);
          }

          if (
            !conditionalProducts.every((sku) =>
              this.cartItemIds.includes(sku)
            ) &&
            coupon.couponCode === uppercaseCode
          ) {
            missingProducts = conditionalProducts
              .filter((sku) => !this.cartItemIds.includes(sku))
              .map((sku) => ({ sku, name: this.products[sku].name }));
            if (missingProducts.length > 0) {
              await this.removePromo(uppercaseCode);
            }
          }

          if (couponDiscounts.some((d) => d.discount === discount.id)) {
            const appliedDiscounts =
              appliedCouponDiscounts[coupon.couponCode] || [];

            appliedCouponDiscounts[coupon.couponCode] = [
              ...appliedDiscounts,
              discount.id
            ];
          } else {
            couponDiscounts.push({
              couponCode: coupon.couponCode,
              discount: discount.id
            });
          }
        });

        if (skippedDiscounts === (coupon?.discounts || []).length) {
          allCouponDiscountsAlreadyApplied = true;
        }
        if (skippedDiscounts > 0) {
          someCouponDiscountsAlreadyApplied = true;
        }
      });

      if (couponExclusive) {
        this.couponCodes = prevCouponCodes;

        await this.save();
      }

      this.calculateTotals();

      if (
        couponForMarketCheck &&
        !isCouponApplicableForMarket(
          couponForMarketCheck,
          this.discounts || [],
          country
        )
      ) {
        await this.removePromo(uppercaseCode);
        return {
          isValid: !!this.couponCodeData?.[uppercaseCode],
          isActive: couponForMarketCheck.active ?? false,
          couponAlreadyApplied,
          allCouponDiscountsAlreadyApplied,
          someCouponDiscountsAlreadyApplied,
          missingProducts,
          couponExclusive,
          minimumTotal,
          minimumTotalCoupon,
          orderTotalTooLow,
          hasNoEffect,
          removedCoupons,
          appliesTo,
          notAvailableInMarket: true
        };
      }

      const isActive = this?.couponCodeData?.[uppercaseCode]?.active || false;
      // couponCodeData[uppercaseCode] is undefined if the coupon code is not valid
      const isValid = !!this?.couponCodeData?.[uppercaseCode];

      if (appliedCouponDiscounts[uppercaseCode]) {
        if (
          appliedCouponDiscounts[uppercaseCode].length ===
          this?.couponCodeData?.[uppercaseCode]?.discounts?.length
        ) {
          allCouponDiscountsAlreadyApplied = true;
        } else {
          someCouponDiscountsAlreadyApplied = true;
        }
      }

      // Check if discounts related to the coupon code are already applied
      const currentCoupon = this.couponCodeData?.[uppercaseCode];
      if (currentCoupon) {
        const currentCouponDiscounts = currentCoupon.discounts || [];
        const fullDiscounts = currentCouponDiscounts
          .map(({ id }) => this.discounts.find((it) => it.id === id))
          .filter((it) => !!it);

        const overrideDiscountsList = fullDiscounts
          .map((discount) => discount?.overrideDiscounts || [])
          .flat()
          .map((discount) => discount.id);

        if (
          this.appliedDiscounts.some((discount) =>
            overrideDiscountsList.includes(discount)
          )
        ) {
          overrideDiscountsList.forEach(async (discount) => {
            const fullDiscount = this.discounts.find((d) => d.id === discount);

            if (fullDiscount) {
              if (fullDiscount.activateCondition === 'couponCode') {
                const couponToRemove = this.coupons?.find((coupon) => {
                  return coupon.discounts.some(
                    (discount) => discount.id === fullDiscount.id
                  );
                });

                if (couponToRemove) {
                  await this.removePromo(couponToRemove.couponCode);
                }
              }
            }
          });
        }
      }

      if (!isValid || allCouponDiscountsAlreadyApplied || hasNoEffect) {
        await this.removePromo(uppercaseCode);
      }

      const minimumTotalDecidingCoupon = this.coupons.reduce(
        (prevCoupon, coupon) => {
          const couponDiscounts = coupon.discounts || [];
          const fullDiscounts = couponDiscounts
            .map(({ id }) => this.discounts.find((it) => it.id === id))
            .filter((it) => !!it);

          const minimumTotal = fullDiscounts.reduce(
            (prevDiscount, discount) => {
              const { priority, minimumOrderTotalAfterDiscount } = discount;
              if (!priority) return prevDiscount;
              if (
                priority < prevDiscount.priority &&
                minimumOrderTotalAfterDiscount
              ) {
                return {
                  couponCode: coupon.couponCode,
                  priority: priority,
                  minimumTotal: minimumOrderTotalAfterDiscount
                };
              }
              return prevDiscount;
            },
            { couponCode: '', minimumTotal: 0, priority: Infinity }
          );

          if (
            minimumTotal.priority < prevCoupon.priority &&
            minimumTotal.minimumTotal
          ) {
            return minimumTotal;
          }

          return prevCoupon;
        },
        { couponCode: '', minimumTotal: 0, priority: Infinity }
      );

      const couponBreakdown = this.calculateCouponBreakdown();

      couponBreakdown.map((d) => {
        if (
          (d.totalSavings === 0 || !d.totalSavings) &&
          !d.appliesTo?.includes('subscription')
        ) {
          removedCoupons.push(d.couponCode);
          this.removePromo(d.couponCode);
        }
      });

      return {
        isValid,
        isActive,
        couponAlreadyApplied,
        allCouponDiscountsAlreadyApplied,
        someCouponDiscountsAlreadyApplied,
        missingProducts,
        couponExclusive,
        minimumTotal,
        minimumTotalCoupon: minimumTotalDecidingCoupon,
        hasNoEffect,
        orderTotalTooLow,
        removedCoupons,
        appliesTo,
        notAvailableInMarket: false
      };
    },
    async removePromo(code: string) {
      this.clearDisplayPricingCache();
      if (this.coupons) {
        this.coupons = this.coupons.filter(
          (coupon) => coupon.couponCode !== code
        );
      }
      if (this.couponCodes) {
        this.couponCodes = this.couponCodes.filter(
          (it) => it.toUpperCase() !== code.toUpperCase()
        );
      }

      // Deprecated promoCode code
      this.promoCode = null;
      this.promoCodeData = {};
      this.promoCodeDescription = [];
      this.promoCodeExclusive = false;
      this.promoCodeDiscounts = [];
      this.promoCodeValid = null;
      this.isReferral = false;
      // End deprecated promoCode code

      this.calculateCouponBreakdown();
      await this.save();
      this.calculateTotals(false);
    },
    /**
     * Uses the discountMap on each cart product to group product discounted amount by discount and group discounts by coupon code.
     *
     */
    calculateCouponBreakdown() {
      let breakdown: DiscountBreakdown = this.discountBreakdown || [];

      if (
        !isEmpty(this.discounts) &&
        !isEmpty(this.cartProducts) &&
        !isEmpty(this.totals)
      ) {
        const { breakdown: discountBreakdown, availableCouponsBreakdown } =
          calculateCartDiscountAmounts({
            appliedDiscounts: this.appliedDiscounts || [],
            discounts: this.discounts || [],
            coupons: this.coupons || [],
            availableCoupons: availableCouponsFromDato || [],
            cartProducts: this.cartProducts || {},
            totals: this.totals.totals || {},
            orderTotals: this.orderTotals ?? undefined
          });
        breakdown = discountBreakdown;
        this.availableCoupons = availableCouponsBreakdown;
      }

      this.discountBreakdown = breakdown;

      return breakdown;
    },
    updateQuizData(newQuizData: Record<string, any>) {
      this.quizData = {
        ...this.quizData,
        ...newQuizData
      };
      this.save();
    },
    calculateTotals(autoSave = true) {
      this.clearDisplayPricingCache();
      const {
        formattedTotals: totals,
        totals: unformattedTotals,
        cartProducts: calculatedCartProducts,
        orderTotals,
        ...calculatedTotals
      } = calculateTotalsFromCart(
        {
          ...this,
          cartProducts: Object.values(this.cartProducts || {})
        } as unknown as CartPayload,
        this.applyCoveCreditAmount
      );

      this.orderTotals = orderTotals;
      this.appliedDiscounts = calculatedTotals.appliedDiscounts ?? [];
      this.totals = {
        ...calculatedTotals,
        orderTotals,
        unformattedTotals,
        formattedTotals: totals,
        totals: unformattedTotals,
        cartProducts: keyBy(calculatedCartProducts, 'sku')
      };

      if (this.totals) {
        this.cartProducts = this.totals.cartProducts;
        this.cartItemIds = this.totals.cartItemIds;
        this.isPayLater = this.totals.isPayLater;
        this.isPremium = this.totals.isPremium;
        this.isValue = this.totals.isValue;
        this.monitoringPlan = this.totals.monitoringPlan;
        this.freeMonths = this.totals.freeMonths;
      }

      if (autoSave) {
        this.autoSave();
      }
      if (typeof window !== 'undefined') {
        const updatedCart = {
          ...this
        };
        if (!isUndefined(updateCallback)) {
          updateCallback({ ...this, ...updatedCart });
        }
      }

      const postcode =
        this.taxPostcode.length === 5
          ? this.taxPostcode
          : this.customer?.postcode || '';

      if (postcode.length === 5 && !this.taxCalculated) {
        this.calculateTax(postcode);
      }

      this.checkExclusiveType();
      this.calculateCouponBreakdown();
      this.calculateZoneCount();
      return totals;
    },
    resolveProductConversionRate(product: ProductRecord): number {
      const productRates = product.conversionRate;
      const cartRates = this.conversionRates || [];
      return (
        productRates?.find((rate) => rate.country === this.country)?.rate ??
        productRates?.find((rate) => rate.country === 'US')?.rate ??
        cartRates.find((rate) => rate.country === this.country)?.rate ??
        cartRates.find((rate) => rate.country === 'US')?.rate ??
        1
      );
    },
    getCartLineUnitPricing(sku: SKU): { retail: number; sale: number } | null {
      const line = this.cartProducts?.[sku];
      if (!line) {
        return null;
      }

      const retail = parseFloat(String(line.price));
      const sale = parseFloat(String(line.discountPrice));

      if (!Number.isFinite(retail) || !Number.isFinite(sale)) {
        return null;
      }

      return { retail, sale };
    },
    runDisplayTotalsLineForSku(sku: SKU, qty: number) {
      try {
        const lineItems = Object.values(this.cartProducts || {}).map(
          (line) => ({
            sku: line.sku,
            qty: line.sku === sku ? qty : line.qty,
            locations: line.locations || []
          })
        );
        if (!lineItems.some((line) => line.sku === sku)) {
          lineItems.push({ sku, qty, locations: [] });
        }

        const payload: CartPayload = JSON.parse(
          JSON.stringify({
            ...this,
            cartProducts: lineItems,
            products: this.products || {},
            discounts: this.discounts || [],
            coupons: this.coupons || [],
            couponCodes: this.couponCodes || []
          })
        );

        const totalsResult = calculateTotalsFromCart(
          payload,
          this.applyCoveCreditAmount
        );
        return (
          totalsResult.cartProducts?.[sku] ??
          Object.values(totalsResult.cartProducts || {}).find(
            (item) => item.sku === sku
          )
        );
      } catch (error) {
        Sentry.captureException(error);
        return null;
      }
    },
    resolveFreePromoSalePrice(sku: SKU, freeDiscount: Discount): number | null {
      const freeCount = freeDiscount.maxQuantity ?? 1;
      const line = this.cartProducts?.[sku];
      const qty = line?.qty ?? 0;

      if (line && qty > 0) {
        const paidQty = qty - Math.min(freeCount, qty);
        const lineTotal = parseFloat(String(line.total));
        if (paidQty > 0 && Number.isFinite(lineTotal)) {
          return parseFloat(roundTo(`${lineTotal / paidQty}`));
        }
      }

      const previewQty = Math.max(freeCount + 1, 2);
      const previewLine = this.runDisplayTotalsLineForSku(sku, previewQty);
      if (!previewLine) {
        return null;
      }

      const previewQtyActual = previewLine.qty ?? previewQty;
      const previewTotal = parseFloat(String(previewLine.total));
      const paidQty = previewQtyActual - Math.min(freeCount, previewQtyActual);
      if (paidQty > 0 && Number.isFinite(previewTotal)) {
        return parseFloat(roundTo(`${previewTotal / paidQty}`));
      }

      return null;
    },
    clearDisplayPricingCache() {
      this.displayPricingCache = {};
      this.displayPricingRevision = null;
    },
    getDisplayPricingRevision(): string {
      const cartProductSnapshot = Object.values(this.cartProducts || {})
        .map((line) => ({
          sku: line.sku,
          qty: line.qty
        }))
        .sort((a, b) => a.sku.localeCompare(b.sku));
      const couponCodes = [...(this.couponCodes || [])].sort();

      return JSON.stringify({
        country: this.country ?? 'US',
        applyCoveCreditAmount: this.applyCoveCreditAmount ?? 0,
        cartProducts: cartProductSnapshot,
        couponCodes
      });
    },
    resolveUnitPricing(
      sku: SKU
    ): { retail: number; sale: number; appliedDiscountIds: string[] } | null {
      const product = this?.products?.[sku];
      if (!product) {
        return null;
      }

      const conversionRate = this.resolveProductConversionRate(product);
      const fallbackRetail = parseFloat(
        roundTo(`${(product.price || 0) * conversionRate}`)
      );
      const fallback = {
        retail: fallbackRetail,
        sale: fallbackRetail,
        appliedDiscountIds: []
      };

      const cartLinePricing = this.getCartLineUnitPricing(sku);
      if (cartLinePricing) {
        const line = this.cartProducts?.[sku];
        const appliedDiscountIds = [...(line?.appliedDiscounts || [])];
        let sale = parseFloat(roundTo(`${cartLinePricing.sale}`));
        const freeDiscount = getFreeUnitsProductDiscount(
          appliedDiscountIds,
          this.discounts || []
        );
        if (freeDiscount) {
          const promoSale = this.resolveFreePromoSalePrice(sku, freeDiscount);
          if (promoSale != null) {
            sale = promoSale;
          }
        }
        return {
          retail: parseFloat(roundTo(`${cartLinePricing.retail}`)),
          sale,
          appliedDiscountIds
        };
      }

      const revision = this.getDisplayPricingRevision();
      const cached = this.displayPricingCache?.[sku];
      if (cached && cached.revision === revision) {
        return {
          retail: cached.retail,
          sale: cached.sale,
          appliedDiscountIds: [...cached.appliedDiscountIds]
        };
      }

      try {
        const line = this.runDisplayTotalsLineForSku(sku, 1);

        const retail = parseFloat(String(line?.price));
        let sale = parseFloat(String(line?.discountPrice));
        const appliedDiscountIds = [...(line?.appliedDiscounts || [])];
        const freeDiscount = getFreeUnitsProductDiscount(
          appliedDiscountIds,
          this.discounts || []
        );
        if (freeDiscount) {
          const promoSale = this.resolveFreePromoSalePrice(sku, freeDiscount);
          if (promoSale != null) {
            sale = promoSale;
          }
        }

        const resolved = {
          retail: Number.isFinite(retail)
            ? parseFloat(roundTo(`${retail}`))
            : fallback.retail,
          sale: Number.isFinite(sale)
            ? parseFloat(roundTo(`${sale}`))
            : fallback.sale,
          appliedDiscountIds
        };

        this.displayPricingCache[sku] = {
          revision,
          ...resolved
        };
        this.displayPricingRevision = revision;

        return resolved;
      } catch (error) {
        Sentry.captureException(error);
        return fallback;
      }
    },
    calculateProductDiscounts(sku: SKU) {
      const product = this?.products?.[sku];

      if (!product) {
        return null;
      }

      const country = (this.country ?? 'US') as 'US' | 'CA';
      const resolved = this.resolveUnitPricing(sku);
      if (!resolved) {
        return null;
      }

      const appliedDiscounts = (resolved.appliedDiscountIds || [])
        .map((id) => this.discounts.find((discount) => discount.id === id))
        .filter(
          (discount): discount is Discount =>
            !!discount && isApplicableForMarket(discount, country)
        );

      const productDiscounts = appliedDiscounts.map((discount) => ({
        salePrice: resolved.sale,
        discount
      }));
      const displayDiscounts = productDiscounts.slice(0, 1);

      return {
        salePrice: roundTo(`${resolved.sale}`),
        displayDiscounts,
        productDiscounts,
        discountBadges: appliedDiscounts
          .map(
            (discount) => discount && generateDiscountBadge(discount, product)
          )
          .filter((d) => !!d)
      };
    },
    getDisplayPrice(sku: SKU): { price: number; salePrice: number } | null {
      const resolved = this.resolveUnitPricing(sku);
      if (!resolved) {
        return null;
      }
      return { price: resolved.retail, salePrice: resolved.sale };
    },
    updateCustomer(updates: Partial<CartCustomer>) {
      this.customer = { ...this.customer, ...updates };
      this.autoSave();
    },
    saveCart() {
      this.isCartSaved = true;
      const updatedCart = {
        ...this
      };
      if (!isUndefined(updateCallback)) {
        updateCallback({ ...updatedCart });
      }
      this.save();
    },
    skipQuiz() {
      this.isQuizSkipped = true;
      const updatedCart = {
        ...this
      };
      if (!isUndefined(updateCallback)) {
        updateCallback({ ...updatedCart });
      }
      this.save();
      this.autoSave();
    },
    setNoSensors(noSensors = false) {
      this.noSensors = noSensors;
      const updatedCart = {
        ...this
      };
      if (!isUndefined(updateCallback)) {
        updateCallback({ ...updatedCart });
      }
      this.autoSave();
    },
    getCartEquipment() {
      const equipment: Array<ProductRecord> = [];

      this.cartItemIds.forEach((sku) => {
        const product = this.products[sku];
        if (!product) return;
        if (product.productType !== 'equipment') return;
        if (PKG_PRODUCTS.includes(sku)) return;
        if (equipment.some((product) => product.sku === sku)) return;
        equipment.push(product);
      });

      return equipment.sort((a, b) => a.position - b.position);
    },
    getEquipmentQuantity() {
      const cartEquipment = this.getCartEquipment();
      let quantityProducts = 0;

      if (cartEquipment.length > 0) {
        quantityProducts = cartEquipment.reduce((total, product) => {
          let productQty = total;
          if (product.qty && product.qty > 0) {
            productQty += 1;
          }
          return productQty;
        }, 0);
      }

      return quantityProducts;
    },
    getSensorQuantity() {
      const cartEquipment = this.getCartEquipment();
      let quantityProducts = 0;

      if (cartEquipment.length > 0) {
        quantityProducts = cartEquipment
          .filter(({ sku }) => !PKG_PRODUCTS.includes(sku))
          .reduce((total, product) => {
            let productQty = total;
            if (product.qty && product.qty > 0) {
              productQty += 1;
            }
            return productQty;
          }, 0);
      }

      return quantityProducts;
    },
    async addEverflow({ id }: { id: string }) {
      this.efTransactionId = id;
      await this.save();
      return true;
    },
    async setAlternativePayment(
      payment: CartAlternativePayment | null,
      getTotals = true
    ) {
      this.alternativePayment = payment;
      syncLegacyAltPaymentFields(this, payment);

      if (getTotals) {
        const totals = this.calculateTotals(false);
        await this.save();
        return totals;
      }
      return true;
    },
    async addAffirm(
      { id, amount, expires }: { id: string; amount: number; expires: number },
      getTotals = true
    ) {
      return this.setAlternativePayment(
        {
          option: 'affirm',
          provider: 'affirm',
          id,
          amount,
          expiresAt: expires
        },
        getTotals
      );
    },
    async removeAffirm(getTotals = true) {
      if (this.alternativePayment?.provider === 'paypal') {
        return getTotals ? this.calculateTotals(false) : true;
      }
      return this.setAlternativePayment(null, getTotals);
    },
    async addPayPal(
      { id, amount, expires }: { id: string; amount: number; expires: number },
      getTotals = true
    ) {
      return this.setAlternativePayment(
        {
          option: 'paypal',
          provider: 'paypal',
          id,
          amount,
          expiresAt: expires
        },
        getTotals
      );
    },
    async removePayPal(getTotals = true) {
      if (this.alternativePayment?.provider === 'affirm') {
        return getTotals ? this.calculateTotals(false) : true;
      }
      return this.setAlternativePayment(null, getTotals);
    },
    setMigratedToAlula(migratedToAlula: boolean) {
      this.migratedToAlula = migratedToAlula;
      this.calculateTotals();
    },
    validate({
      checkCart = false,
      defaultCart = false,
      checkCustomer = false,
      checkMonitoring = false
      // excludeCamera = false
    }) {
      const result = {
        emptyCart: false,
        cartChanged: false,
        invalidCustomerInfo: false,
        missingMonitoring: false,
        noSensors: false
      };
      if (
        !isUndefined(this.cartItemIds) &&
        !isUndefined(this.products) &&
        !isEmpty(this.products)
      ) {
        const hasPackage = Object.values(this?.cartProducts || {}).some(
          (product) => {
            return product.productType === 'package';
          }
        );

        // Check if cart empty
        if (checkCart) {
          if (this.getEquipmentQuantity() === 0 && !hasPackage) {
            result.emptyCart = true;
          }

          if (this.getSensorQuantity() === 0) {
            result.noSensors = true;
          }
        }

        if (
          Object.values(this.cartProducts || {}).some(
            (product) => product.productType === 'package'
          )
        ) {
          const initialItems = [
            { sku: 'CPA-HUB-345-1', qty: 0 },
            { sku: 'CP-YS-23i-1', qty: 0 },
            { sku: 'window-sticker-sm', qty: 0 }
          ];

          this.updateCartItems(initialItems, false);
          result.cartChanged = true;
        }

        /**
         * Default Cart
         */
        if (
          ((checkCart && !result.emptyCart && !result.cartChanged) ||
            defaultCart) &&
          missingInitialProducts({
            products: this.products,
            cartItemIds: this.cartItemIds,
            cartProducts: this.cartProducts
          })
        ) {
          const initialItems = [
            { sku: 'CPA-HUB-345-1', qty: 1 },
            { sku: 'CPA-PANEL-345-1', qty: 1 },
            { sku: 'CP-YS-23i-1', qty: 1 },
            { sku: 'window-sticker-sm', qty: 3 }
          ];

          console.log('initial items not found, adding...', initialItems);
          this.updateCartItems(initialItems, false);

          if (
            isUndefined(this.monitoringPlan) ||
            isEmpty(this.monitoringPlan)
          ) {
            this.updateMonitoringPlan('vm-pn', false);
          }

          const autoSaveFn = this.getAutoSaveQueuedFn();
          if (autoSaveFn) {
            clearTimeout(autoSaveFn);
          }
          const funcId = setTimeout(() => {
            this.setAutoSaveQueuedFn(null);
            this.calculateTotals();
          }, 4000);
          this.setAutoSaveQueuedFn(funcId);

          result.cartChanged = true;
        }

        if (checkMonitoring) {
          if (
            isUndefined(this.monitoringPlan) ||
            isEmpty(this.monitoringPlan) ||
            !this.monitoringPlan.sku ||
            isUndefined(this.products[this.monitoringPlan.sku]) ||
            isEmpty(this.products[this.monitoringPlan.sku]) ||
            this.products[this.monitoringPlan.sku].qty !== 1 ||
            isUndefined(this.cartProducts[this.monitoringPlan.sku]) ||
            isEmpty(this.cartProducts[this.monitoringPlan.sku]) ||
            this.cartProducts[this.monitoringPlan.sku].qty !== 1
          ) {
            result.missingMonitoring = true;
          }
        }

        // Check Customer is valid
        if (checkCustomer) {
          const customer =
            JSON.parse(localStore.getItem('customer-info')) || {};

          if (isUndefined(customer?.isValid) || customer?.isValid !== true) {
            result.invalidCustomerInfo = true;
          }
        }
      }

      return result;
    },
    autoSave() {
      if (!this.getAutoSaveQueuedFn()) {
        const funcId = setTimeout(() => {
          this.setAutoSaveQueuedFn(null);
          this.save();
        }, 3000);
        this.setAutoSaveQueuedFn(funcId);
      }
    },
    setAutoSaveQueuedFn(funcId: ReturnType<typeof setTimeout> | null) {
      AUTO_SAVE_QUEUE_FN = funcId;
    },
    getAutoSaveQueuedFn() {
      return AUTO_SAVE_QUEUE_FN;
    },
    lastSave: null as Promise<{
      data: CoveCartFromBackend;
    }> | null,
    async save(update = true) {
      const start = performance.now();
      this.loading = true;
      if (debug) {
        log(`Loading: ${this.loading}`);
        console.trace('Saving Cart');
      }
      if (!isUndefined(updateCallback)) {
        updateCallback({ ...this });
      }

      const autoSaveFn = this.getAutoSaveQueuedFn();
      if (autoSaveFn) {
        clearTimeout(autoSaveFn);
      }

      const currentCartProducts = Object.values(
        JSON.parse(JSON.stringify(this?.cartProducts))
      ) as Array<CartProduct>;
      const keyCartProducts = keyBy(
        currentCartProducts?.filter?.(({ qty }) => qty > 0),
        'sku'
      );

      const body = {
        ...omitCartLookupCatalogs(this),
        cartProducts: keyCartProducts
      };

      if (update) {
        persistCartLocally(cartLocalStorageKey, this);
      }
      const cartId = body.cartId;

      if (!cartId) {
        return;
      }

      const currentSave = saveCart(cartId, body, { apiV4Url })
        .then((res) => {
          this.loading = false;
          if (debug) {
            log(`Loading: ${this.loading}`);
          }
          return res;
        })
        .catch((error) => {
          /* log to loggly */
          Sentry.captureException(error);
        }) as Promise<{
        data: CoveCartFromBackend;
      }>;

      this.lastSave = currentSave;
      const result = await currentSave;
      const end = performance.now();
      const time = end - start;
      this.lastSaveDuration = time;
      if (debug) {
        log(`Last Save: ${time}ms`);
      }

      if (!update || this.lastSave !== currentSave) {
        return;
      }

      this.lastSave = null;
      const { data: updatedCart } = result || {};
      if (updatedCart) {
        this.updateCart(updatedCart);
      }
      persistCartLocally(cartLocalStorageKey, this);
    },
    updateCartMetaData(
      metadata: Partial<{
        isPayLaterEligible: boolean;
        blacklistedPostcode: boolean;
        returningUser: boolean;
      }> = {}
    ) {
      const {
        // isPayLaterEligible = true,
        blacklistedPostcode = false,
        returningUser = false
      } = metadata;

      if (this.blacklistedPostcode !== blacklistedPostcode) {
        this.blacklistedPostcode = blacklistedPostcode;
      }
      if (this.returningUser !== returningUser) {
        this.returningUser = returningUser;
      }
    },
    updateCart(
      data: Omit<SavedCart, 'totals' | 'products' | 'discounts' | 'payLater'> & {
        totals: SavedCartTotals;
        products?: SavedCart['products'];
        discounts?: SavedCart['discounts'];
        payLater?: SavedCart['payLater'];
      }
    ) {
      this.updateCartMetaData(data.metadata);

      const basicChecks = [
        'appliedDiscounts',
        'hasExclusiveCoupon',
        'hasValidCouponCode',
        'salesRepId'
      ] as const;

      basicChecks.forEach((key) => {
        if (this && data) {
          if (this[key] && data[key] && !isEqual(this[key], data[key])) {
            //@ts-expect-error this
            this[key] = data[key];
          }
        }
      });

      if (Array.isArray(data.discounts)) {
        this.discounts = data.discounts.map((it) => {
          const discount = this.discounts.find(
            (discount) => discount.id === it.id
          );
          return {
            ...(discount ? { ...discount, ...it } : it)
          };
        });
      }
      this.orderTotals = data.orderTotals;
      this.equipmentTaxRate = data.equipmentTaxRate || 0;
      this.monitoringTaxRate = data.monitoringTaxRate || 0;

      const postcode = data?.taxPostcode
        ? data?.taxPostcode
        : data?.customer?.postcode || '';

      if (postcode.length >= 5 && !this.taxCalculated) {
        this.calculateTax(postcode);
      }

      if (
        !isEqual(this.coupons, data.coupons) ||
        !isEqual(this.couponCodes, data.couponCodes) ||
        !isEqual(this.couponCodeData, data.couponCodeData) ||
        (data.couponCodes || []).length !== (data.coupons || []).length
      ) {
        this.couponCodes = data.couponCodes;
        this.couponCodeData = data.couponCodeData;
        this.coupons = Object.values(data.couponCodeData || {}).map(
          (coupon) => {
            return coupon;
          }
        );
      }

      if (
        Object.keys(data.couponCodeData || {}).length !==
        (data.couponCodes || []).length
      ) {
        this.couponCodes = Object.keys(data.couponCodeData);
      }

      const catalogDiscounts = Array.isArray(data.discounts)
        ? data.discounts
        : this.discounts;

      if (
        !isEmpty(data.appliedDiscounts) &&
        !isEmpty(catalogDiscounts) &&
        !isEmpty(data.coupons) &&
        !isEmpty(this.cartProducts) &&
        !isEmpty(data.totals)
      ) {
        const { breakdown, remove, availableCouponsBreakdown } =
          calculateCartDiscountAmounts({
            appliedDiscounts: data.appliedDiscounts || [],
            discounts: catalogDiscounts || [],
            coupons: data.coupons || [],
            availableCoupons: availableCouponsFromDato || [],
            cartProducts: this.cartProducts || {},
            totals: data.totals || {},
            orderTotals: data.orderTotals
          });

        if (remove.length > 0) {
          remove.forEach((code) => this.removePromo(code));
        }

        this.discountBreakdown = breakdown;
        this.availableCoupons = availableCouponsBreakdown;
      }

      this.calculateTotals(false);
      if (!isUndefined(updateCallback)) {
        updateCallback({ ...this });
      }
    },
    async clear(newCartId?: string) {
      if (typeof window !== 'undefined') {
        this.loading = true;
        await this.save(false);
        if (localStore.removeItem) {
          localStore.removeItem(cartIdLocalStorageKey);
          localStore.removeItem(cartLocalStorageKey);
          localStore.removeItem('quiz-completed');
          localStore.removeItem('lastCheckoutRoute');
        }
        if (updateCallback) {
          updateCallback(
            await coveCart({
              updateCallback,
              apiV4Url,
              ...(newCartId ? { loadRemote: true } : { loadRemote }),
              salesRepId: this.salesRepId,
              referralCode: this.referralCode,
              datoData,
              prefixLocalStorageKey,
              shippingMethod,
              ...(newCartId ? { cartId: newCartId } : {})
            })
          );
        }
      }
    }
  };

  persistCartLocally(cartLocalStorageKey, Cart);
  localStore.setItem(cartIdLocalStorageKey, Cart.cartId);
  migrateCartData(Cart);

  Cart.calculateTotals();
  Cart.dismissZoneCountWarning = Cart.dismissZoneCountWarning.bind(Cart);
  return Cart;
};

export default coveCart;

/**
 * Catalog fields used only as local calculateTotals lookup data. They are
 * loaded from DatoCMS in-process and must not be posted on cart saves
 * or written to localStorage.
 */
function omitCartLookupCatalogs(cart: Record<string, any> = {}) {
  const rest = { ...cart };
  delete rest.products;
  delete rest.discounts;
  delete rest.payLater;
  return rest;
}

function persistCartLocally(key: string, cart: Record<string, any>) {
  localStore.setItem(key, JSON.stringify(omitCartLookupCatalogs(cart)));
}

async function defaultSaveCart(
  cartId: string,
  body: any,
  { apiV4Url }: { apiV4Url?: string } = {}
) {
  return axios.post(`${apiV4Url}/cart/${cartId}`, body, {
    headers: {
      'content-type': 'application/json'
    }
  }) as Promise<{
    data: CoveCartFromBackend;
  }>;
}

const logStyle =
  'background: #002334; color: #fff; border-left: 4px solid #3722d3; padding: 2px 8px;';

const log = (message = '') => {
  console.log(`%c${message}`, logStyle);
};

export { defaultSaveCart, log, omitCartLookupCatalogs };
